import React, { useCallback, useRef, useState } from 'react';
import { VariablePicker } from '../components/VariablePicker';
import { focusEditable } from '../utils/focusUtils';
import {
  buildVariableHtml,
  refreshVariableHtml,
  replaceTriggerWithHtml,
} from '../utils/variableUtils';
import {
  placeCaretAfterAtomic,
  pinCaretAfterPillIndex,
  schedulePlaceCaretAfterAtomic,
} from '../utils/atomicCaret';

/**
 * "{{" variable picker for contenteditables (text, checkbox, table cells…).
 * Captures the caret text node before the picker steals focus, then splices
 * a refreshed pill in place of the trigger.
 *
 * For normal blocks pass contentEditableRef + setContent + onUpdate.
 * For table cells (or other custom hosts) pass commitHtml / restoreFocus
 * instead — contentEditableRef still points at the active editable element.
 */
export const useVariablePicker = ({
  blockId,
  contentEditableRef,
  contentRef,
  setContent,
  onUpdate,
  resolveVariable,
  availableVariables = [],
  onCreateVariable = null,
  /** Optional: (html) => void — used instead of setContent/onUpdate */
  commitHtml = null,
  /** Optional: () => void — used instead of focusEditable(blockId) on cancel */
  restoreFocus = null,
}) => {
  const [showVarPicker, setShowVarPicker] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const textNodeRef = useRef(null);
  const triggerRef = useRef({ trigger: '{{', queryLength: 0 });

  const closeVarPicker = useCallback(
    ({ restoreFocus: doRestore = true } = {}) => {
      setShowVarPicker(false);
      textNodeRef.current = null;
      // Skip restore after insert — caret was already placed after the pill
      if (!doRestore) return;
      if (restoreFocus) restoreFocus();
      else if (blockId) focusEditable(blockId, { preventScroll: true });
    },
    [blockId, restoreFocus]
  );

  const insertVariablePill = useCallback(
    (name, index = null) => {
      const el = contentEditableRef?.current;
      if (el) {
        const pillHtml = refreshVariableHtml(
          buildVariableHtml(name, { index, trailingSpace: false }),
          resolveVariable
        );
        const { trigger, queryLength } = triggerRef.current;
        const placed = replaceTriggerWithHtml(
          el,
          textNodeRef.current,
          trigger,
          queryLength,
          pillHtml
        );
        if (!placed) {
          el.innerHTML = `${el.innerHTML}${pillHtml}`;
          el.focus({ preventScroll: true });
          const pill = el.querySelector('[data-doc-variable]:last-of-type');
          if (pill) placeCaretAfterAtomic(pill);
        }
        // Index of the pill we just inserted — survives ContentEditable's
        // innerHTML rewrite (which otherwise dumps the caret at block end,
        // very noticeable in code blocks).
        const pills = el.querySelectorAll('[data-doc-variable]');
        const pillIndex = pills.length - 1;
        const value = el.innerHTML;
        if (commitHtml) {
          commitHtml(value);
        } else {
          if (contentRef) contentRef.current = value;
          setContent?.(value);
          onUpdate?.(blockId, { content: value });
        }
        if (pillIndex >= 0) {
          pinCaretAfterPillIndex(el, pillIndex);
          schedulePlaceCaretAfterAtomic(
            () => contentEditableRef?.current,
            (host) => host?.querySelectorAll('[data-doc-variable]')[pillIndex]
          );
        }
      }
      closeVarPicker({ restoreFocus: false });
    },
    [
      blockId,
      closeVarPicker,
      commitHtml,
      contentEditableRef,
      contentRef,
      onUpdate,
      resolveVariable,
      setContent,
    ]
  );

  /**
   * Call from onKeyDown. Returns true when "{{" was detected and the picker
   * is being opened (caller can early-return).
   */
  const tryOpenFromBraceKey = useCallback((e) => {
    if (e.key !== '{') return false;
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return false;
    const range = selection.getRangeAt(0);
    const textBeforeCursor =
      range.startContainer.textContent?.substring(0, range.startOffset) || '';
    if (!textBeforeCursor.endsWith('{')) return false;

    // Wait for the second "{" to land in the DOM, then snapshot caret.
    setTimeout(() => {
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return;
      const r = sel.getRangeAt(0);
      const rect = r.getBoundingClientRect();
      textNodeRef.current = r.startContainer;
      triggerRef.current = { trigger: '{{', queryLength: 0 };
      setPosition({
        x: rect.left,
        y: rect.bottom + 4,
        caretTop: rect.top,
      });
      setShowVarPicker(true);
    }, 0);
    return true;
  }, []);

  /**
   * Open from the slash menu (`/variable`). Uses the captured "/" text node
   * so the insert can splice out `/query` the same way "{{" is replaced.
   */
  const openFromSlash = useCallback((position, { textNode, queryLength } = {}) => {
    textNodeRef.current = textNode || null;
    triggerRef.current = { trigger: '/', queryLength: queryLength || 0 };
    if (position) setPosition(position);
    setShowVarPicker(true);
  }, []);

  const variablePicker = showVarPicker ? (
    <VariablePicker
      position={position}
      variables={(availableVariables || []).filter((v) => !v.shadowed)}
      onPick={({ name, index }) => insertVariablePill(name, index)}
      onCreate={
        onCreateVariable
          ? (name, type) => {
              onCreateVariable(name, type);
              insertVariablePill(name);
            }
          : null
      }
      onClose={closeVarPicker}
    />
  ) : null;

  return { tryOpenFromBraceKey, openFromSlash, variablePicker, showVarPicker };
};
